import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useStoreState } from "ezStore";
import { intl } from "LocaleWrapper";
import { useQuery } from "@apollo/client";

import { Button, PageHeader, Card, FormGroup } from "@cofano/cck";

import { Box, CircularProgress, Grid2 as Grid, Tab, Tabs } from "@mui/material";

import { getCanSeeCargoDocuments } from "components/documents/DocumentsUtils";
import Helmet from "components/helmet/Helmet";
import useTabs from "components/hooks/useTabs";
import Documents, { DocumentOptionalColumns } from "components/documents/Documents";
import CommentsWidget from "components/comments/CommentsWidget";
import useComments from "components/comments/useComments";

import { DataFormat, Fields, hasOptionalFieldPermission, hasPluginPermission, Language, usePermission } from "imports/Utils";

import { LocationLink, ProductLink, RelationLink, ShipLink, VoyageLink } from "utils/LinkUtils";
import { FormattedDate } from "utils/DateUtils";

import StatusChanger from "view/statuses/components/changer/StatusChanger";
import ContainerBulkEditDialog from "view/containers/components/dialogs/ContainerBulkEditDialog";
import { FullOrEmpty } from "view/containers/utils/ContainerUtils";

import { CargoTabs } from "./utils/CargoUtils";
import CargoActionsDetails from "./components/details/CargoActionsDetails";

import { ChildProps } from "types/commons";

import { GetAllAdnDocument, GetCargoDocument, GetCargoQuery } from "__generated__/view/cargos/graphql/cargo.queries.generated";
import { DocumentEntity, StatusType, WeightToleranceType } from "__generated__/enums";

export type CargoFromQuery = ChildProps<GetCargoQuery, "cargo">;

const Cargo = () => {

	const navigate = useNavigate();
	const { id } = useParams();

	const session = useStoreState(state => state.session);

	const { selectedTab, selectTab } = useTabs<CargoTabs>({
		defaultSelected: CargoTabs.Actions,
	});

	const { isRTR, isSkipperOrShipManager, isOperator, isTerminal, isReadOnly, isCustomer } = usePermission();

	const [ bulkEditDialog, setBulkEditDialog ] = useState<{ open: boolean, selection: number[] }>({
		open: false,
		selection: [],
	});

	const fieldEnabled = (field: string) => {
		return (
			(isRTR || isOperator || isTerminal || isCustomer) &&
			hasOptionalFieldPermission(session, Fields.ORDER, field)
		);
	};

	const { data: adnData } = useQuery(GetAllAdnDocument);
	const unNumbers = adnData?.allAdn ?? [];

	const { data, refetch } = useQuery(GetCargoDocument, {
		variables: {
			id: Number(id),
		},
	});

	const { commentsProps } = useComments({
		collapsableDisabled: true,
		cargos: data?.cargo ? [ { ...data.cargo } ] : [],
	});

	return (
		<div>
			<PageHeader>
				<PageHeader.Title>
					{Language("CARGO")}
					<Helmet title={intl.formatMessage({ id: "CARGO" })} />
				</PageHeader.Title>

				<PageHeader.Actions>
					{data?.cargo && (
						<React.Fragment>
							<Button onClick={() => navigate("/view/cargos")} variant="outlined">
								{intl.formatMessage({ id: "CARGOS" })}
							</Button>

							<Button onClick={() => navigate(`/view/spotsalescharters/${data.cargo?.charter?.id}/details`)} variant="outlined">
								{intl.formatMessage({ id: "CARGO_CHARTER" })}
							</Button>

							{!isReadOnly && !hasPluginPermission(session, "externalCargosManagement") && isRTR && (
								<Button onClick={() => navigate(`/view/cargos/${data.cargo?.id}/edit`)}>
									{intl.formatMessage({ id: "EDIT" })}
								</Button>
							)}

							{!isReadOnly && !hasPluginPermission(session, "externalCargosManagement") && (isSkipperOrShipManager) && (
								<Button
									onClick={() => {
										setBulkEditDialog({
											open: true,
											selection: [ data.cargo?.id as number ],
										});
									}}>
									{intl.formatMessage({ id: "EDIT" })}
								</Button>
							)}
						</React.Fragment>
					)}
				</PageHeader.Actions>
			</PageHeader>

			{data?.cargo ? (
				<Grid container spacing={3}>
					<Grid
						size={{
							xs: 12,
							sm: 12,
							md: 12,
							lg: 5,
						}}>
						<Card>
							<FormGroup label={"ID"} labelPlacement={"left-center"} overflow="hidden">
								{data.cargo.id}
							</FormGroup>
							{(isSkipperOrShipManager || fieldEnabled("containerNumber")) && (
								<FormGroup label={Language("CONTAINER_NUMBER")} labelPlacement="left-center" overflow="hidden">
									{data.cargo.containerNumber || "-"}
								</FormGroup>
							)}

							{fieldEnabled("masterBundleContainerNumber") && (
								<FormGroup label={Language("CARGO_MASTER_BUNDLE_CONTAINER_NUMBER")} labelPlacement="left-center" overflow="hidden">
									{data.cargo.masterBundleContainerNumber || "-"}
								</FormGroup>
							)}

							{fieldEnabled("cargoNumber") && (
								<FormGroup label={Language("CARGO_NUMBER")} labelPlacement="left-center" overflow="hidden">
									{data.cargo.cargoNumber || "-"}
								</FormGroup>
							)}

							<FormGroup label={Language("CHARTER_ORDER_NUMBER")} labelPlacement="left-center" overflow="hidden">
								{data.cargo.charter?.charterNumber || "-"}
							</FormGroup>

							<FormGroup label={Language("CLIENT")} labelPlacement="left-center" overflow="hidden">
								{data.cargo.charter?.debtor.name || "-"}
							</FormGroup>

							{fieldEnabled("nominationNumber") && (
								<FormGroup label={Language("NOMINATION_NUMBER")} labelPlacement="left-center" overflow="hidden">
									{data.cargo.nominationNumber || "-"}
								</FormGroup>
							)}

							{fieldEnabled("customerReference") && (
								<FormGroup label={Language("CUSTOMER_REFERENCE")} labelPlacement="left-center" overflow="hidden">
									{DataFormat.remarks(data.cargo.charter?.customerReference, "-")}
								</FormGroup>
							)}

							<FormGroup label={Language("SHIP")} labelPlacement="left-center" overflow="hidden">
								{data.cargo.voyage?.ship ? <ShipLink ship={data.cargo.voyage.ship} /> : "-"}
							</FormGroup>

							<FormGroup label={Language("VOYAGE")} labelPlacement="left-center" overflow="hidden">
								{data.cargo.voyage ? <VoyageLink voyage={data.cargo.voyage} /> : "-"}
							</FormGroup>

							<FormGroup label={Language("STATUS")} labelPlacement="left-center" overflow="hidden">
								<StatusChanger
									entity={data.cargo}
									statusType={StatusType.Cargo}
									refetch={refetch}
									showHistory />
							</FormGroup>

							{!hasPluginPermission(session, "useContainers") && (
								<FormGroup label={Language("PRODUCT")} labelPlacement="left-center" overflow="hidden">
									<ProductLink product={data.cargo.product} />
								</FormGroup>
							)}

							{hasOptionalFieldPermission(session, Fields.ORDER, "containerOperator") && (
								<FormGroup label={Language("CONTAINER_OPERATOR")} labelPlacement="left-center" overflow="hidden">
									<RelationLink relation={data.cargo.containerOperator} />
								</FormGroup>
							)}

							{fieldEnabled("productDetails") && (
								<FormGroup label={Language("PRODUCT_DETAILS")} labelPlacement="left-center" overflow="hidden">
									{data.cargo.productDetails || "-"}
								</FormGroup>
							)}

							<FormGroup label={Language("LOADING_REFERENCE")} labelPlacement="left-center" overflow="hidden">
								{data.cargo.loadingReference || "-"}
							</FormGroup>

							<FormGroup label={Language("DISCHARGING_REFERENCE")} labelPlacement="left-center" overflow="hidden">
								{data.cargo.dischargingReference || "-"}
							</FormGroup>

							{fieldEnabled("containerType") && (
								<FormGroup label={Language("CARGO_CONTAINER_TYPE")} labelPlacement="left-center" overflow="hidden">
									{DataFormat.containerType(data.cargo.containerType)}
								</FormGroup>
							)}

							{fieldEnabled("weight") && (
								<FormGroup label={Language("CARGO_WEIGHT")} labelPlacement="left-center" overflow="hidden">
									{DataFormat.tonnage(data.cargo.weight)}
								</FormGroup>
							)}

							{fieldEnabled("volume") && (
								<FormGroup label={Language("CARGO_VOLUME")} labelPlacement="left-center" overflow="hidden">
									{DataFormat.tonnage(data.cargo.volume)}
								</FormGroup>
							)}

							{fieldEnabled("tareWeight") && (
								<FormGroup label={Language("CARGO_TARE_WEIGHT")} labelPlacement="left-center" overflow="hidden">
									{DataFormat.tonnage(data.cargo.tareWeight)}
								</FormGroup>
							)}

							{(isSkipperOrShipManager || fieldEnabled("grossWeight")) && (
								<FormGroup label={Language("CARGO_GROSS_WEIGHT")} labelPlacement="left-center" overflow="hidden">
									{DataFormat.tonnage(data.cargo.grossWeight)}
								</FormGroup>
							)}

							{(isSkipperOrShipManager || fieldEnabled("verifiedGrossWeight")) && (
								<FormGroup label={Language("CARGO_VERIFIED_GROSS_WEIGHT")} labelPlacement="left-center" overflow="hidden">
									{DataFormat.tonnage(data.cargo.verifiedGrossWeight)}
								</FormGroup>
							)}

							{[ WeightToleranceType.CharterersOption, WeightToleranceType.OwnersOption ].includes(data.cargo.weightToleranceType!) && <FormGroup label={Language("DECLARED_FINAL_WEIGHT")} labelPlacement="left-center">
								{DataFormat.simpleUnit(data.cargo.declaredFinalWeight, DataFormat.UNITS.MTON)}
							</FormGroup>}

							{fieldEnabled("weightTolerance") &&
								<FormGroup label={Language("WEIGHT_TOLERANCE_TYPE")} labelPlacement="left-center" overflow="hidden">
									{data.cargo.weightToleranceType}
								</FormGroup>
							}

							{[ WeightToleranceType.CharterersOption, WeightToleranceType.OwnersOption ].includes(data.cargo.weightToleranceType!) && <FormGroup label={Language("WEIGHT_OPTION_TYPE")} labelPlacement="left-center">
								{data.cargo.weightOptionType}
							</FormGroup>}

							{[ WeightToleranceType.CharterersOption, WeightToleranceType.OwnersOption ].includes(data.cargo.weightToleranceType!) && <FormGroup label={Language("WEIGHT_OPTION_PERCENTAGE")} labelPlacement="left-center">
								{DataFormat.simpleUnit(data.cargo.weightOptionPercentage, "%")}
							</FormGroup>}

							{fieldEnabled("empty") &&
								<FormGroup label={Language("CARGO_FULL_OR_EMPTY")} labelPlacement="left-center" overflow="hidden">
									{DataFormat.containerIsEmpty(data.cargo.empty)}
								</FormGroup>
							}


							{fieldEnabled("desiredTemperature") && (
								<FormGroup label={Language("CARGO_DESIRED_TEMPERATURE")} labelPlacement="left-center" overflow="hidden">
									{DataFormat.temperature(data.cargo.desiredTemperature) || "-"}
								</FormGroup>
							)}

							{fieldEnabled("temperatureRangeLower") && (
								<FormGroup label={Language("CARGO_TEMPERATURE_RANGE_LOWER")} labelPlacement="left-center" overflow="hidden">
									{DataFormat.temperature(data.cargo.temperatureRangeLower)}
								</FormGroup>
							)}

							{fieldEnabled("temperatureRangeUpper") && (
								<FormGroup label={Language("CARGO_TEMPERATURE_RANGE_UPPER")} labelPlacement="left-center" overflow="hidden">
									{DataFormat.temperature(data.cargo.temperatureRangeUpper)}
								</FormGroup>
							)}

							{(isSkipperOrShipManager || fieldEnabled("sealNumber")) && (
								<FormGroup label={Language("CARGO_ISSEALED")} labelPlacement="left-center" overflow="hidden">
									{DataFormat.boolean(data.cargo.sealed)}
								</FormGroup>
							)}

							{(isSkipperOrShipManager || fieldEnabled("sealNumber")) && (
								<FormGroup label={Language("CARGO_SEALNUMBER")} labelPlacement="left-center" overflow="hidden">
									{data.cargo.sealNumber || "-"}
								</FormGroup>
							)}

							{fieldEnabled("waNumber") &&
								<FormGroup label={Language("WA_NUMBER")} labelPlacement="left-center" overflow="hidden">
									{data.cargo.waNumber || "-"}
								</FormGroup>
							}

							{fieldEnabled("reference") && (
								<FormGroup label={Language("CARGO_REFERENCE")} labelPlacement="left-center" overflow="hidden">
									{data.cargo.reference || "-"}
								</FormGroup>
							)}

							{fieldEnabled("bookingReference") && (
								<FormGroup label={Language("CARGO_BOOKING_REFERENCE")} labelPlacement="left-center" overflow="hidden">
									{data.cargo.bookingReference || "-"}
								</FormGroup>
							)}

							{hasPluginPermission(session, "useContainers") && <>
								<FormGroup label={Language("CARGO_LOADED")} labelPlacement="left-center" overflow="hidden">
									<FormattedDate date={data.cargo.loaded} withoutYear />
								</FormGroup>

								<FormGroup label={Language("CARGO_DISCHARGED")} labelPlacement="left-center" overflow="hidden">
									<FormattedDate date={data.cargo.discharged} withoutYear />
								</FormGroup>
							</>}

							{fieldEnabled("transportType") && (
								<FormGroup label={Language("CARGO_TRANSPORT_TYPE")} labelPlacement="left-center" overflow="hidden">
									{data.cargo.transportType || "-"}
								</FormGroup>
							)}

							{fieldEnabled("unNumbers") && (
								<FormGroup label={Language("CARGO_UN_NUMBER")} labelPlacement="left-center" overflow="hidden">
									{data.cargo.unNumbers && data.cargo.unNumbers?.length > 0 ? data.cargo.unNumbers.map(item => (unNumbers as { identifier: string }[]).find(u => u.identifier === item?.unNumberId))
										.map(unNumber => unNumber?.identifier || intl.formatMessage({ id: "UNKNOWN" }))
										.join(", ") : "-"}
								</FormGroup>
							)}

							{fieldEnabled("services") && <>
								<FormGroup label={Language("HOSE_RENTAL")} labelPlacement="left-center" overflow="hidden">
									{DataFormat.booleanAsText(data.cargo.isHoseRentalRequired)}
								</FormGroup>

								<FormGroup label={Language("NITROGEN_PURGING")} labelPlacement="left-center" overflow="hidden">
									{DataFormat.booleanAsText(data.cargo.isNitrogenPurgingRequired)}
								</FormGroup>

								<FormGroup label={Language("NITROGEN_BLANKETING_REQUIRED")} labelPlacement="left-center" overflow="hidden">
									{DataFormat.booleanAsText(data.cargo.isNitrogenBlanketingRequired)}
								</FormGroup>

								<FormGroup label={Language("INHIBITED_PRODUCT")} labelPlacement="left-center" overflow="hidden">
									{DataFormat.booleanAsText(data.cargo.isInhibitedProduct)}
								</FormGroup>

								<FormGroup label={Language("TEMPERATURE_CONTROL_REQUIRED")} labelPlacement="left-center" overflow="hidden">
									{DataFormat.booleanAsText(data.cargo.isTemperatureControlRequired)}
								</FormGroup>

								<FormGroup label={Language("TANK_CLEANING")} labelPlacement="left-center" overflow="hidden">
									{DataFormat.booleanAsText(data.cargo.isCleaningRequired)}
								</FormGroup>
							</>}


							<FormGroup label={Language("WATER_LEVEL_OPTIMISED")} labelPlacement="left-center" overflow="hidden">
								{data.cargo.waterLevelOptimised || "-"}
							</FormGroup>

							{hasPluginPermission(session, "useContainers") && (
								<FormGroup label={Language("CARGO_DANGEROUS_GOOD")} labelPlacement="left-center" overflow="hidden">
									{DataFormat.boolean(data.cargo.hasDangerousGoods)}
								</FormGroup>
							)}

							{fieldEnabled("reeferCheckType") && (
								<FormGroup label={Language("REEFER_CHECK_TYPE")} labelPlacement="left-center" overflow="hidden">
									{data.cargo.reeferCheckType || "-"}
								</FormGroup>
							)}

							{fieldEnabled("imoClass") && (
								<FormGroup label={Language("CARGO_IMO_CLASS")} labelPlacement="left-center" overflow="hidden">
									{data.cargo.imoClass || "-"}
								</FormGroup>
							)}

							{fieldEnabled("billOfLading") && (
								<FormGroup label={Language("BL_NUMBER")} labelPlacement="left-center" overflow="hidden">
									{data.cargo.billOfLadingNumber || "-"}
								</FormGroup>
							)}

							{fieldEnabled("humidity") && (
								<FormGroup label={Language("CARGO_REEFER_HUMIDITY")} labelPlacement="left-center" overflow="hidden">
									{DataFormat.percentage(data.cargo.humidity)}
								</FormGroup>
							)}

							{fieldEnabled("remarks") && (
								<FormGroup label={Language("CARGO_REMARKS")} labelPlacement="left-center" overflow="hidden">
									{DataFormat.remarks(data.cargo.remarks, null)}
								</FormGroup>
							)}

							{fieldEnabled("carrierScac") && (
								<FormGroup label={Language("CARGO_CARRIER_SCAC_LOADING")} labelPlacement="left-center" overflow="hidden">
									{data.cargo.scacCode || "-"}
								</FormGroup>
							)}

							{fieldEnabled("carrierScacDischarging") && (
								<FormGroup label={Language("CARGO_CARRIER_SCAC_DISCHARGING")} labelPlacement="left-center" overflow="hidden">
									{data.cargo.scacCodeDischarging || "-"}
								</FormGroup>
							)}

							{(isSkipperOrShipManager || fieldEnabled("damage")) && (
								<FormGroup label={Language("CARGO_DAMAGE")} labelPlacement="left-center" overflow="hidden">
									{data.cargo.damage || "-"}
								</FormGroup>
							)}

							{fieldEnabled("releasedForModality") && (
								<FormGroup label={Language("CARGO_RELEASED_FOR_BARGE")} labelPlacement="left-center" overflow="hidden">
									{DataFormat.boolean(data.cargo.releasedForBarge)}
								</FormGroup>
							)}

							{fieldEnabled("releasedForModality") && (
								<FormGroup label={Language("CARGO_RELEASED_FOR_TRAIN")} labelPlacement="left-center" overflow="hidden">
									{DataFormat.boolean(data.cargo.releasedForTrain)}
								</FormGroup>
							)}

							{fieldEnabled("releasedForModality") && (
								<FormGroup label={Language("CARGO_RELEASED_FOR_TRUCK")} labelPlacement="left-center" overflow="hidden">
									{DataFormat.boolean(data.cargo.releasedForTruck)}
								</FormGroup>
							)}

							{fieldEnabled("deliveryCondition") && (
								<FormGroup label={Language("DELIVERY_CONDITION")} labelPlacement="left-center" overflow="hidden">
									{data.cargo.deliveryCondition?.description || "-"}
								</FormGroup>
							)}

							{fieldEnabled("revenue") && (
								<FormGroup label={Language("REVENUE")} labelPlacement="left-center" overflow="hidden">
									{DataFormat.price(data.cargo.revenue) || "-"}
								</FormGroup>
							)}

							{fieldEnabled("connectingVisitLoadingCallReferenceNumber") && (
								<FormGroup label={Language("CONNECTING_VISIT_LOADING_CALL_REFERENCE")} labelPlacement="left-center" overflow="hidden">
									{data.cargo.connectingVisitLoadingCallReferenceNumber || "-"}
								</FormGroup>
							)}

							{fieldEnabled("connectingVisitDischargingCallReferenceNumber") && (
								<FormGroup label={Language("CONNECTING_VISIT_DISCHARGING_CALL_REFERENCE")} labelPlacement="left-center" overflow="hidden">
									{data.cargo.connectingVisitDischargingCallReferenceNumber || "-"}
								</FormGroup>
							)}
						</Card>
					</Grid>

					<Grid
						size={{
							xs: 12,
							sm: 12,
							md: 12,
							lg: 7,
						}}>
						<Grid container spacing={3}>
							<Grid size={12}>
								<Card disablePadding>
									<Box sx={{ borderBottom: 1, borderColor: "divider" }}>
										<Tabs
											variant='scrollable'
											value={selectedTab}
											onChange={(event, newValue) => selectTab(newValue)}>
											<Tab label={Language("CARGO_ACTIONS")} value={CargoTabs.Actions} />
											<Tab label={Language("RELATIONS")} value={CargoTabs.Relations} />
											{fieldEnabled("outOfGauge") && (
												<Tab label={Language("CARGO_OOG")} value={CargoTabs.Oog} />
											)}

											{fieldEnabled("deepsea") && (
												<Tab label={Language("CARGO_DEEPSEA_CONNECTION")} value={CargoTabs.Deepsea} />
											)}

											{fieldEnabled("voyageInfo") && (
												<Tab label={Language("VOYAGE_SCHEDULE")} value={CargoTabs.VoyageSchedule} />
											)}
										</Tabs>
									</Box>

									{selectedTab === CargoTabs.Actions && (
										<React.Fragment>
											<CargoActionsDetails cargo={data.cargo} />
										</React.Fragment>
									)}

									{selectedTab === CargoTabs.Relations && (
										<Box p={2}>
											<CargoRelations cargo={data.cargo} />
										</Box>
									)}

									{selectedTab === CargoTabs.Oog && fieldEnabled("outOfGauge") && (
										<Box p={2}>
											<OutOfGauge cargo={data.cargo} />
										</Box>
									)}

									{selectedTab === CargoTabs.Deepsea && fieldEnabled("deepsea") && (
										<Box p={2}>
											<Deepsea cargo={data.cargo} />
										</Box>
									)}

									{selectedTab === CargoTabs.VoyageSchedule && fieldEnabled("voyageInfo") && (
										<Box p={2}>
											<VoyageSchedule cargo={data.cargo} />
										</Box>
									)}
								</Card>
							</Grid>

							{getCanSeeCargoDocuments(session) && (
								<Grid size={12}>
									<Card title={Language("ORDER_DOCUMENTS")}>
										<Documents
											entities={[ {
												id: data.cargo.id,
												name: DocumentEntity.Cargo,
												type: DocumentEntity.Cargo,
											} ]}
											showOptionalColumns={[
												DocumentOptionalColumns.IssuedAt,
												DocumentOptionalColumns.ValidUntil,
											]} />
									</Card>
								</Grid>
							)}
						</Grid>
					</Grid>
				</Grid>
			) : (
				<React.Fragment>
					<CircularProgress size={30} />
				</React.Fragment>
			)}

			<ContainerBulkEditDialog
				open={bulkEditDialog.open}
				onClose={() => {
					setBulkEditDialog({
						open: false,
						selection: [],
					});
				}}
				containerIds={bulkEditDialog.selection}
				defaultValues={{
					containerNumber: data?.cargo?.containerNumber ?? "",
					isEmpty: data?.cargo?.empty ? FullOrEmpty.Empty : FullOrEmpty.Full,
					temperature: data?.cargo?.desiredTemperature ?? null,
					grossWeight: data?.cargo?.grossWeight ?? null,
					verifiedGrossWeight: data?.cargo?.verifiedGrossWeight ?? null,
					sealNumber: data?.cargo?.sealNumber ?? "",
					damage: data?.cargo?.damage ?? "",
					canSkipperEditLoadedTimestamp: false,
					canSkipperEditDischargedTimestamp: false,
				}}
				refetch={refetch}
			/>

			<CommentsWidget {...commentsProps} />
		</div>
	);
};

const OutOfGauge = ({ cargo }: { cargo: CargoFromQuery }) => {
	return (
		<React.Fragment>
			<FormGroup label={Language("CARGO_OOG_OVER_HEIGHT")} labelPlacement="left-center" overflow="hidden">
				{DataFormat.simpleUnit(cargo.overheight, DataFormat.UNITS.CENTIMETER)}
			</FormGroup>

			<FormGroup label={Language("CARGO_OOG_OVER_LENGTH")} labelPlacement="left-center" overflow="hidden">
				{DataFormat.simpleUnit(cargo.overlength, DataFormat.UNITS.CENTIMETER)}
			</FormGroup>

			<FormGroup label={Language("CARGO_OOG_OVER_LENGTH_BEHIND")} labelPlacement="left-center" overflow="hidden">
				{DataFormat.simpleUnit(cargo.overlengthBehind, DataFormat.UNITS.CENTIMETER)}
			</FormGroup>

			<FormGroup label={Language("CARGO_OOG_OVER_WIDTH_RIGHT")} labelPlacement="left-center" overflow="hidden">
				{DataFormat.simpleUnit(cargo.overwidthRight, DataFormat.UNITS.CENTIMETER)}
			</FormGroup>

			<FormGroup label={Language("CARGO_OOG_OVER_WIDTH_LEFT")} labelPlacement="left-center" overflow="hidden">
				{DataFormat.simpleUnit(cargo.overwidthLeft, DataFormat.UNITS.CENTIMETER)}
			</FormGroup>

			<FormGroup label={Language("CARGO_OOG_DOOR_CLOSED")} labelPlacement="left-center" overflow="hidden">
				{DataFormat.boolean(cargo.doorOpen) || "-"}
			</FormGroup>

			<FormGroup label={Language("CARGO_OOG_CHAINS")} labelPlacement="left-center" overflow="hidden">
				{DataFormat.boolean(cargo.chains) || "-"}
			</FormGroup>

			<FormGroup label={Language("CARGO_OOG_GENERATOR")} labelPlacement="left-center" overflow="hidden">
				{DataFormat.boolean(cargo.generator) || "-"}
			</FormGroup>
		</React.Fragment>
	);
};

const Deepsea = ({ cargo }: { cargo: CargoFromQuery }) => {
	return (
		<React.Fragment>
			<FormGroup label={Language("CARGO_DEEPSEA_SHIP_NAME_LOADING")} labelPlacement="left-center" overflow="hidden">
				{cargo.deepseaShipNameLoading || "-"}
			</FormGroup>

			<FormGroup label={Language("CARGO_DEEPSEA_SHIP_IMO_LOADING")} labelPlacement="left-center" overflow="hidden">
				{cargo.deepseaShipImoLoading || "-"}
			</FormGroup>

			<FormGroup label={Language("CARGO_DEEPSEA_VOYAGE_NUMBER_LOADING")} labelPlacement="left-center" overflow="hidden">
				{cargo.deepseaVoyageNumberLoading || "-"}
			</FormGroup>

			<FormGroup label={Language("CARGO_DEEPSEA_ETA_LOADING")} labelPlacement="left-center" overflow="hidden">
				<FormattedDate date={cargo.deepseaEtaLoading} withoutYear />
			</FormGroup>

			<FormGroup label={Language("CARGO_DEEPSEA_SHIP_NAME_DISCHARGING")} labelPlacement="left-center" overflow="hidden">
				{cargo.deepseaShipNameDischarging || "-"}
			</FormGroup>

			<FormGroup label={Language("CARGO_DEEPSEA_SHIP_IMO_DISCHARGING")} labelPlacement="left-center" overflow="hidden">
				{cargo.deepseaShipImoDischarging || "-"}
			</FormGroup>

			<FormGroup label={Language("CARGO_DEEPSEA_VOYAGE_NUMBER_DISCHARGING")} labelPlacement="left-center" overflow="hidden">
				{cargo.deepseaVoyageNumberDischarging || "-"}
			</FormGroup>

			<FormGroup label={Language("CARGO_DEEPSEA_ETA_DISCHARGING")} labelPlacement="left-center" overflow="hidden">
				<FormattedDate date={cargo.deepseaEtaDischarging} withoutYear />
			</FormGroup>

			<FormGroup label={Language("CARGO_DEEPSEA_ESTIMATE_DISCHARGE")} labelPlacement="left-center" overflow="hidden">
				<FormattedDate date={cargo.deepseaEstimateDischarge} withoutYear />
			</FormGroup>
		</React.Fragment>
	);
};

const CargoRelations = ({ cargo }: { cargo: CargoFromQuery }) => {
	const session = useStoreState(state => state.session);

	return (
		<>
			<FormGroup label={Language("SUPPLIER")} labelPlacement="left-center" overflow="hidden">
				<RelationLink relation={cargo.supplier} />
			</FormGroup>

			<FormGroup label={Language("CARGO_CONSIGNOR")} labelPlacement="left-center" overflow="hidden">
				<RelationLink relation={cargo.consignor} />
			</FormGroup>

			<FormGroup label={Language("ORDER_CONSIGNEE")} labelPlacement="left-center" overflow="hidden">
				<RelationLink relation={cargo.consignee} />
			</FormGroup>

			{hasOptionalFieldPermission(session, Fields.ORDER, "containerOperator") && (
				<FormGroup label={Language("CONTAINER_OPERATOR")} labelPlacement="left-center" overflow="hidden">
					<RelationLink relation={cargo.containerOperator} />
				</FormGroup>
			)}
		</>
	);
};

const VoyageSchedule = ({ cargo }: { cargo: CargoFromQuery }) => {
	return (
		<React.Fragment>
			<FormGroup label={Language("CARGO_PREVIOUS_INLAND_TERMINAL")} labelPlacement="left-center" overflow="hidden">
				<LocationLink location={cargo.previousInlandTerminal} />
			</FormGroup>

			<FormGroup label={Language("CARGO_NEXT_INLAND_TERMINAL")} labelPlacement="left-center" overflow="hidden">
				<LocationLink location={cargo.nextInlandTerminal} />
			</FormGroup>

			<FormGroup label={Language("CARGO_PREVIOUS_PORT")} labelPlacement="left-center" overflow="hidden">
				{cargo.previousPort || "-"}
			</FormGroup>

			<FormGroup label={Language("CARGO_NEXT_PORT")} labelPlacement="left-center" overflow="hidden">
				{cargo.nextPort || "-"}
			</FormGroup>

			<FormGroup label={Language("CARGO_FINAL_PORT")} labelPlacement="left-center" overflow="hidden">
				{cargo.finalPort || "-"}
			</FormGroup>
		</React.Fragment>
	);
};

export default Cargo;
